import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import axios from 'axios';
import { Plus, X, UserCog, Edit, Trash2, KeyRound } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const departments = [
  { value: 'sales', label: 'Sales', color: '#0D47A1' },
  { value: 'support', label: 'Technical Support', color: '#D32F2F' },
  { value: 'marketing', label: 'Marketing', color: '#059669' },
  { value: 'partners', label: 'Partners Contact Point', color: '#7C3AED' },
];

interface TeamMember {
  _id: string; name: string; email: string; department: string; position: string; phone: string; status: string; created_at?: string;
}

export default function AdminTeam() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [resetId, setResetId] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '', department: 'sales', position: '', phone: '' });
  const [editForm, setEditForm] = useState({ name: '', department: '', position: '', phone: '', status: 'active', email: '' });
  const [saving, setSaving] = useState(false);
  const [filterDept, setFilterDept] = useState('all');

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try { const { data } = await axios.get(`${API}/api/admin/team`); setMembers(data); } catch {}
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true);
    try {
      await axios.post(`${API}/api/admin/team`, form);
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', department: 'sales', position: '', phone: '' });
      loadMembers();
    } catch (err: any) { alert(err.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  const handleEdit = async () => {
    if (!editId) return; setSaving(true);
    try {
      await axios.put(`${API}/api/admin/team/${editId}`, editForm);
      setEditId(null); loadMembers();
    } catch {} finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this team member?')) return;
    try { await axios.delete(`${API}/api/admin/team/${id}`); loadMembers(); } catch {}
  };

  const handleResetPassword = async () => {
    if (!resetId || resetPassword.length < 6) return; setSaving(true);
    try {
      await axios.post(`${API}/api/admin/team/${resetId}/reset-password`, { password: resetPassword });
      setResetId(null); setResetPassword('');
      alert('Password reset successful');
    } catch {} finally { setSaving(false); }
  };

  const openEdit = (m: TeamMember) => {
    setEditId(m._id);
    setEditForm({ name: m.name, department: m.department, position: m.position, phone: m.phone, status: m.status, email: m.email });
  };

  const filtered = filterDept === 'all' ? members : members.filter(m => m.department === filterDept);

  const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', border: '1px solid var(--border)', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' };

  const getDeptColor = (d: string) => departments.find(x => x.value === d)?.color || '#475569';
  const getDeptLabel = (d: string) => departments.find(x => x.value === d)?.label || d;

  const ModalOverlay = ({ children, onClose }: { children: React.ReactNode; onClose: () => void }) => (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} onClick={onClose} />
      <div style={{ position: 'relative', background: 'white', border: '1px solid var(--border)', padding: 32, width: '100%', maxWidth: 500, zIndex: 51, maxHeight: '90vh', overflowY: 'auto' }}>
        {children}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar type="admin" active="team" />
      <main data-testid="admin-team-page" style={{ flex: 1, background: 'var(--bg-portal)', padding: 32 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <UserCog size={28} style={{ color: 'var(--primary)' }} />
              <div>
                <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 28, fontWeight: 700 }}>Team Management</h1>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{members.length} members</p>
              </div>
            </div>
            <button data-testid="add-team-member-btn" onClick={() => setShowCreate(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', background: 'var(--primary)', color: 'white', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', textTransform: 'uppercase' }}>
              <Plus size={16} /> Add Member
            </button>
          </div>

          {/* Department filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
            <button onClick={() => setFilterDept('all')}
              style={{ padding: '8px 16px', fontSize: 12, fontWeight: 600, border: '1px solid var(--border)', background: filterDept === 'all' ? '#0A1128' : 'white', color: filterDept === 'all' ? 'white' : 'var(--text-secondary)', cursor: 'pointer', textTransform: 'uppercase' }}>
              All ({members.length})
            </button>
            {departments.map(d => {
              const count = members.filter(m => m.department === d.value).length;
              return (
                <button key={d.value} onClick={() => setFilterDept(d.value)}
                  style={{ padding: '8px 16px', fontSize: 12, fontWeight: 600, border: `1px solid ${filterDept === d.value ? d.color : 'var(--border)'}`, background: filterDept === d.value ? `${d.color}14` : 'white', color: filterDept === d.value ? d.color : 'var(--text-secondary)', cursor: 'pointer' }}>
                  {d.label} ({count})
                </button>
              );
            })}
          </div>

          {/* Members grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {filtered.map(m => (
              <div key={m._id} data-testid={`team-member-${m._id}`} style={{ background: 'white', border: '1px solid var(--border)', overflow: 'hidden' }}>
                <div style={{ height: 4, background: getDeptColor(m.department) }} />
                <div style={{ padding: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <p style={{ fontWeight: 700, fontSize: 16 }}>{m.name}</p>
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{m.position || m.department}</p>
                    </div>
                    <span style={{ padding: '3px 8px', fontSize: 10, fontWeight: 700, background: m.status === 'active' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', color: m.status === 'active' ? '#16A34A' : '#DC2626', textTransform: 'uppercase' }}>
                      {m.status}
                    </span>
                  </div>
                  <span style={{ display: 'inline-block', padding: '3px 10px', fontSize: 10, fontWeight: 600, background: `${getDeptColor(m.department)}12`, color: getDeptColor(m.department), textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
                    {getDeptLabel(m.department)}
                  </span>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
                    <span>{m.email}</span>
                    {m.phone && <span>{m.phone}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button data-testid={`edit-member-${m._id}`} onClick={() => openEdit(m)}
                      style={{ flex: 1, padding: '6px', fontSize: 11, fontWeight: 600, border: '1px solid var(--border)', background: 'white', color: 'var(--primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                      <Edit size={12} /> Edit
                    </button>
                    <button onClick={() => { setResetId(m._id); setResetPassword(''); }}
                      style={{ padding: '6px 10px', fontSize: 11, fontWeight: 600, border: '1px solid var(--border)', background: 'white', color: '#D97706', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <KeyRound size={12} />
                    </button>
                    <button data-testid={`delete-member-${m._id}`} onClick={() => handleDelete(m._id)}
                      style={{ padding: '6px 10px', fontSize: 11, fontWeight: 600, border: '1px solid var(--border)', background: 'white', color: '#DC2626', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Create Modal */}
          {showCreate && (
            <ModalOverlay onClose={() => setShowCreate(false)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 700 }}>Add Team Member</h2>
                <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
              </div>
              <form onSubmit={handleCreate} data-testid="create-team-form">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div><label style={labelStyle}>Full Name *</label><input data-testid="team-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required style={inputStyle} /></div>
                  <div><label style={labelStyle}>Email *</label><input data-testid="team-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required style={inputStyle} /></div>
                  <div><label style={labelStyle}>Password *</label><input data-testid="team-password" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required style={inputStyle} /></div>
                  <div><label style={labelStyle}>Department *</label>
                    <select data-testid="team-department" value={form.department} onChange={e => setForm({...form, department: e.target.value})} style={{...inputStyle, cursor: 'pointer'}}>
                      {departments.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </div>
                  <div><label style={labelStyle}>Position</label><input data-testid="team-position" value={form.position} onChange={e => setForm({...form, position: e.target.value})} placeholder="e.g. Sales Manager" style={inputStyle} /></div>
                  <div><label style={labelStyle}>Phone</label><input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} style={inputStyle} /></div>
                  <button type="submit" data-testid="team-submit" disabled={saving}
                    style={{ padding: '12px', background: 'var(--primary)', color: 'white', fontWeight: 600, border: 'none', textTransform: 'uppercase', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                    {saving ? '...' : 'Add Member'}
                  </button>
                </div>
              </form>
            </ModalOverlay>
          )}

          {/* Edit Modal */}
          {editId && (
            <ModalOverlay onClose={() => setEditId(null)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 700 }}>Edit Member</h2>
                <button onClick={() => setEditId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div><label style={labelStyle}>Name</label><input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>Email</label><input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>Department</label>
                  <select value={editForm.department} onChange={e => setEditForm({...editForm, department: e.target.value})} style={{...inputStyle, cursor: 'pointer'}}>
                    {departments.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>Position</label><input value={editForm.position} onChange={e => setEditForm({...editForm, position: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>Phone</label><input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>Status</label>
                  <select value={editForm.status} onChange={e => setEditForm({...editForm, status: e.target.value})} style={{...inputStyle, cursor: 'pointer'}}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <button onClick={handleEdit} disabled={saving}
                  style={{ padding: '12px', background: 'var(--primary)', color: 'white', fontWeight: 600, border: 'none', textTransform: 'uppercase', cursor: 'pointer' }}>
                  {saving ? '...' : 'Save Changes'}
                </button>
              </div>
            </ModalOverlay>
          )}

          {/* Reset Password Modal */}
          {resetId && (
            <ModalOverlay onClose={() => setResetId(null)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 700 }}>Reset Password</h2>
                <button onClick={() => setResetId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div><label style={labelStyle}>New Password *</label><input type="password" value={resetPassword} onChange={e => setResetPassword(e.target.value)} placeholder="Min. 6 characters" style={inputStyle} /></div>
                <button onClick={handleResetPassword} disabled={saving || resetPassword.length < 6}
                  style={{ padding: '12px', background: '#D97706', color: 'white', fontWeight: 600, border: 'none', textTransform: 'uppercase', cursor: 'pointer', opacity: resetPassword.length < 6 ? 0.5 : 1 }}>
                  Reset Password
                </button>
              </div>
            </ModalOverlay>
          )}
        </div>
      </main>
    </div>
  );
}
